import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

import { localDateStr, weekMonday } from './utils';

function UtilBar({ pct }) {
  const color = pct >= 95 ? 'var(--critical)' : pct >= 80 ? 'var(--warn)' : 'var(--ok)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 99, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 38, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

export default function CapacityReport() {
  const [weekStart, setWeekStart] = useState(weekMonday(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (ws) => {
    setLoading(true);
    fetch(`${API}/api/reports/capacity?week_start=${ws}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(weekStart); }, [weekStart]);

  const changeWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(localDateStr(d)); // useEffect fires load()
  };

  const exportCSV = () => {
    window.location.href = `${API}/api/reports/export/csv?type=capacity&week_start=${weekStart}`;
  };

  const printReport = () => window.print();

  return (
    <div>
      <ReportNote what="Team availability against planned and logged work." weekStart={weekStart} />
      {/* Controls */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => changeWeek(-1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, minWidth: 180, textAlign: 'center' }}>
            {data ? data.week_label : weekStart}
          </span>
          <button onClick={() => changeWeek(1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={exportCSV} className="btn btn-secondary" style={{ gap: 6 }}>
            ↓ CSV
          </button>
          <button onClick={printReport} className="btn btn-secondary" style={{ gap: 6 }}>
            🖨 Print / PDF
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading capacity data…</div>}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <div className="grid-4" style={{ marginBottom: 24 }}>
            <div className="card">
              <div className="card-title">Week Capacity / Person</div>
              <div className="card-value">{data.cap_hours}h</div>
              <div className="card-sub">Mon–Thu 8h · Fri 7h (standard schedule)</div>
            </div>
            <div className="card">
              <div className="card-title">Team Total Capacity</div>
              <div className="card-value">{Math.round(data.team_cap_secs / 3600)}h</div>
              <div className="card-sub">{data.members.length} team members, individual schedules applied</div>
            </div>
            <div className="card">
              <div className="card-title">Team Hours Logged</div>
              <div className="card-value" style={{ color: 'var(--accent)' }}>{data.team_log_display}</div>
              <div className="card-sub">excl. absences</div>
            </div>
            <div className="card">
              <div className="card-title">Holidays This Week</div>
              <div className="card-value" style={{ color: data.holidays.length ? 'var(--warn)' : 'var(--text-3)' }}>
                {data.holidays.length}
              </div>
              <div className="card-sub">{data.holidays.length ? data.holidays.join(', ') : 'None'}</div>
            </div>
          </div>

          {/* Per-member table */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18, fontFamily: 'Syne, sans-serif' }}>
              Capacity per Team Member — {data.week_label}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ textAlign: 'right' }}>Available</th>
                    <th style={{ textAlign: 'right' }}>Logged</th>
                    <th style={{ textAlign: 'right' }}>Absence</th>
                    <th style={{ textAlign: 'right' }}>Remaining</th>
                    <th style={{ minWidth: 160 }}>Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map(m => {
                    const color = m.utilization >= 95 ? 'var(--critical)' : m.utilization >= 80 ? 'var(--warn)' : 'var(--ok)';
                    return (
                      <tr key={m.name}>
                        <td style={{ fontWeight: 600 }}>{m.name}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{m.cap_hours}h</td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--accent)' }}>{m.log_display}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--warn)' }}>
                          {m.abs_secs > 0 ? m.abs_display : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color }}>
                          {m.remaining_display}
                        </td>
                        <td style={{ minWidth: 160 }}><UtilBar pct={m.utilization} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-3)', borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', gap: 20 }}>
              <span>🟢 &lt;80% — comfortable</span>
              <span>🟡 80–95% — getting full</span>
              <span>🔴 &gt;95% — overloaded</span>
              <span style={{ marginLeft: 'auto' }}>ABS worklogs = legal holiday / illness</span>
            </div>
          </div>
        </>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .topbar { display: none !important; }
          .main { margin: 0 !important; padding: 20px !important; }
        }
      `}</style>
    </div>
  );
}
